import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Edit as EditIcon,
  FolderOpen as CaseIcon,
  Group as BulkIcon
} from '@mui/icons-material';
import { apiService } from '../services/apiService';

const FALLBACK_OPTIONS = {
  statuses: ['New', 'In Progress', 'Under Review', 'Resolved', 'False Positive', 'Closed'],
  severities: ['Low', 'Medium', 'High', 'Critical']
};

/**
 * Bulk actions dialog for the detections list.
 *
 * Props:
 *  - open            : boolean – controls dialog visibility (parent owns it)
 *  - initialAction   : '' | 'bulk-update' | 'create-case' – step shown when opened
 *  - selectedDetections : detection objects
 *  - onBulkUpdate()  : called after a successful bulk update
 *  - onCreateCase(c) : called with the created case after a successful case creation
 *  - onClose()       : parent should set open=false
 *  - users           : [{_id, firstName, lastName}]
 *  - options         : {statuses, severities} from GET /detections/options (optional)
 */
const DetectionBulkActions = ({
  open = false,
  initialAction = '',
  selectedDetections = [],
  onBulkUpdate,
  onCreateCase,
  onClose,
  users = [],
  options
}) => {
  const [action, setAction] = useState(initialAction);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fetchedOptions, setFetchedOptions] = useState(null);

  const [bulkUpdateForm, setBulkUpdateForm] = useState({ status: '', assignedTo: '', severity: '' });
  const [caseForm, setCaseForm] = useState({ title: '', description: '', priority: 'Medium', assignedTo: '' });

  const opts = options || fetchedOptions || FALLBACK_OPTIONS;
  const statusOptions = opts.statuses || FALLBACK_OPTIONS.statuses;
  const severityOptions = opts.severities || FALLBACK_OPTIONS.severities;
  const priorityOptions = severityOptions;

  // Load option lists only if the parent did not provide them
  useEffect(() => {
    if (options || fetchedOptions || !open) return;
    apiService.detections.getOptions()
      .then((res) => setFetchedOptions(res.data))
      .catch(() => setFetchedOptions(FALLBACK_OPTIONS));
  }, [open, options, fetchedOptions]);

  // Reset step + messages every time the dialog is opened
  useEffect(() => {
    if (open) {
      setAction(initialAction || '');
      setError(null);
      setSuccess(null);
    }
  }, [open, initialAction]);

  useEffect(() => {
    if (selectedDetections.length > 0) {
      setCaseForm((prev) => ({
        ...prev,
        title: `Investigation: ${selectedDetections.length} Related Detections`,
        description: `Case created from ${selectedDetections.length} detections: ${selectedDetections.slice(0, 3).map((d) => d.title).join(', ')}${selectedDetections.length > 3 ? '...' : ''}`
      }));
    }
  }, [selectedDetections]);

  const detectionIds = () => selectedDetections.map((d) => d._id || d.id);

  const handleBulkUpdate = async () => {
    const updates = {};
    if (bulkUpdateForm.status) updates.status = bulkUpdateForm.status;
    if (bulkUpdateForm.assignedTo) updates.assignedTo = bulkUpdateForm.assignedTo;
    if (bulkUpdateForm.severity) updates.severity = bulkUpdateForm.severity;

    if (Object.keys(updates).length === 0) {
      setError('Please select at least one field to update');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.detections.bulkUpdate({ detectionIds: detectionIds(), updates });
      setSuccess(response.data.message || `Successfully updated ${response.data.modifiedCount} detections`);
      setBulkUpdateForm({ status: '', assignedTo: '', severity: '' });
      if (onBulkUpdate) onBulkUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update detections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCase = async () => {
    if (!caseForm.title.trim()) {
      setError('Case title is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.detections.createCase({
        detectionIds: detectionIds(),
        title: caseForm.title.trim(),
        description: caseForm.description,
        priority: caseForm.priority,
        assignedTo: caseForm.assignedTo || undefined
      });
      setSuccess(`Case created successfully with ${response.data.linkedDetections} detection(s)`);
      setCaseForm({ title: '', description: '', priority: 'Medium', assignedTo: '' });
      if (onCreateCase) onCreateCase(response.data.case);
    } catch (err) {
      const data = err.response?.data;
      let message = data?.message || 'Failed to create case';
      if (data?.linkedDetections?.length) {
        message += `: ${data.linkedDetections.map((d) => d.title).join(', ')}`;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const userMenuItems = (emptyLabel) => [
    <MenuItem key="" value="">{emptyLabel}</MenuItem>,
    ...users.map((u) => (
      <MenuItem key={u._id} value={u._id}>
        {u.firstName} {u.lastName}
      </MenuItem>
    ))
  ];

  const renderActionContent = () => {
    switch (action) {
      case 'bulk-update':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Bulk Update Detections</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Update {selectedDetections.length} selected detection(s)
            </Typography>

            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={bulkUpdateForm.status}
                  onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="">No Change</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Assign To</InputLabel>
                <Select
                  value={bulkUpdateForm.assignedTo}
                  onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, assignedTo: e.target.value })}
                  label="Assign To"
                >
                  {userMenuItems('No Change')}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={bulkUpdateForm.severity}
                  onChange={(e) => setBulkUpdateForm({ ...bulkUpdateForm, severity: e.target.value })}
                  label="Severity"
                >
                  <MenuItem value="">No Change</MenuItem>
                  {severityOptions.map((severity) => (
                    <MenuItem key={severity} value={severity}>{severity}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        );

      case 'create-case':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Create Case from Detections</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Create a new case linking {selectedDetections.length} detection(s)
            </Typography>

            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Case Title"
                value={caseForm.title}
                onChange={(e) => setCaseForm({ ...caseForm, title: e.target.value })}
                fullWidth
                required
                inputProps={{ maxLength: 200 }}
              />

              <TextField
                label="Description"
                value={caseForm.description}
                onChange={(e) => setCaseForm({ ...caseForm, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
                inputProps={{ maxLength: 2000 }}
              />

              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={caseForm.priority}
                    onChange={(e) => setCaseForm({ ...caseForm, priority: e.target.value })}
                    label="Priority"
                  >
                    {priorityOptions.map((priority) => (
                      <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select
                    value={caseForm.assignedTo}
                    onChange={(e) => setCaseForm({ ...caseForm, assignedTo: e.target.value })}
                    label="Assign To"
                  >
                    {userMenuItems('Unassigned')}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>Selected Detections:</Typography>
              <List dense>
                {selectedDetections.slice(0, 5).map((detection, index) => (
                  <ListItem key={detection._id || detection.id} divider={index < Math.min(selectedDetections.length, 5) - 1}>
                    <ListItemText
                      primary={detection.title}
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <Box display="flex" gap={1} alignItems="center">
                          <Chip label={detection.severity} size="small" />
                          <Chip label={detection.status} size="small" />
                          {detection.case && <Chip label="Already in a case" size="small" color="warning" />}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {selectedDetections.length > 5 && (
                  <ListItem>
                    <ListItemText
                      primary={`... and ${selectedDetections.length - 5} more detections`}
                      secondary="All selected detections will be linked to the case"
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        );

      default:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Bulk Actions</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose an action to perform on {selectedDetections.length} selected detection(s)
            </Typography>

            <Box display="flex" flexDirection="column" gap={2} mt={3}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setAction('bulk-update')}
                fullWidth
                sx={{ justifyContent: 'flex-start', p: 2 }}
              >
                <Box textAlign="left">
                  <Typography variant="subtitle1">Bulk Update</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update status, assignment, or severity for all selected detections
                  </Typography>
                </Box>
              </Button>

              <Button
                variant="outlined"
                startIcon={<CaseIcon />}
                onClick={() => setAction('create-case')}
                fullWidth
                sx={{ justifyContent: 'flex-start', p: 2 }}
              >
                <Box textAlign="left">
                  <Typography variant="subtitle1">Create Case</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a new case and link all selected detections to it
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
        );
    }
  };

  const renderActions = () => {
    if (action === 'bulk-update' || action === 'create-case') {
      const isCase = action === 'create-case';
      return (
        <>
          <Button onClick={() => setAction('')} disabled={loading}>Back</Button>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button
            onClick={isCase ? handleCreateCase : handleBulkUpdate}
            variant="contained"
            disabled={loading || selectedDetections.length === 0}
          >
            {loading ? <CircularProgress size={20} /> : (isCase ? 'Create Case' : 'Update Detections')}
          </Button>
        </>
      );
    }
    return <Button onClick={onClose}>Close</Button>;
  };

  return (
    <Dialog open={!!open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BulkIcon />
          Bulk Actions ({selectedDetections.length} selected)
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
        )}
        {renderActionContent()}
      </DialogContent>

      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
};

export default DetectionBulkActions;
